// Performance monitoring utility for API calls
interface PerformanceMetrics {
    url: string;
    duration: number;
    timestamp: Date;
    status: 'success' | 'error';
}

class PerformanceMonitor {
    private metrics: PerformanceMetrics[] = [];
    private readonly maxMetrics = 100;

    logMetric(url: string, duration: number, status: 'success' | 'error') {
        const metric: PerformanceMetrics = {
            url,
            duration,
            timestamp: new Date(),
            status
        };

        this.metrics.push(metric);

        // Keep only the last maxMetrics entries
        if (this.metrics.length > this.maxMetrics) {
            this.metrics = this.metrics.slice(-this.maxMetrics);
        }

        // Log slow calls
        if (duration > 1000) {
            console.warn(`🐌 Slow API call: ${url} (${duration}ms)`);
        } else if (duration < 200) {
            // ...existing code...
        }
    }

    getAverageResponseTime(url?: string): number {
        const relevantMetrics = url
            ? this.metrics.filter(m => m.url.includes(url))
            : this.metrics;

        if (relevantMetrics.length === 0) return 0;

        const total = relevantMetrics.reduce((sum, metric) => sum + metric.duration, 0);
        return total / relevantMetrics.length;
    }

    getSlowestCalls(limit: number = 10): PerformanceMetrics[] {
        return [...this.metrics]
            .sort((a, b) => b.duration - a.duration)
            .slice(0, limit);
    }

    getRecentMetrics(minutes: number = 5): PerformanceMetrics[] {
        const cutoff = new Date(Date.now() - minutes * 60 * 1000);
        return this.metrics.filter(m => m.timestamp > cutoff);
    }

    generateReport(): void {
        console.group('📊 Performance Report');
        // ...existing code...

        const slow = this.getSlowestCalls(5);
        if (slow.length > 0) {
            // ...existing code...
            slow.forEach(_metric => {
                // ...existing code...
            });
        }

        // Performance by endpoint
        const endpointStats = new Map();
        this.metrics.forEach(metric => {
            const endpoint = metric.url.split('?')[0]; // Remove query params
            if (!endpointStats.has(endpoint)) {
                endpointStats.set(endpoint, { total: 0, count: 0 });
            }
            const stats = endpointStats.get(endpoint);
            stats.total += metric.duration;
            stats.count += 1;
        });

        // ...existing code...
        Array.from(endpointStats.entries())
            .sort((a, b) => (b[1].total / b[1].count) - (a[1].total / a[1].count))
            .slice(0, 10)
            .forEach(([_endpoint, _stats]) => {
                // ...existing code...
            });

        console.groupEnd();
    }

    clear(): void {
        this.metrics = [];
    }
}

export const performanceMonitor = new PerformanceMonitor();

// Auto-generate report every 5 minutes in development
if (import.meta.env.DEV) {
    setInterval(() => {
        if (performanceMonitor.getRecentMetrics().length > 0) {
            performanceMonitor.generateReport();
        }
    }, 5 * 60 * 1000);
}
